import React from "react";
import * as MUI from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";
import SimpleAlert from "../../../../../../components/display/SimpleAlert";
import { trimText } from "../../../../../../utils/Functions";

const CELL_TRIM_LENGTH = 40;

export default function WarehouseNamedEntityTable({
  items,
  isLoading,
  emptyMessage,
  onEdit,
  onDelete,
  testIdPrefix,
}) {
  const { t } = useTranslation();

  return (
    <MUI.Paper
      variant="outlined"
      sx={{ borderRadius: 3, borderColor: "divider", overflow: "hidden" }}
    >
      <MUI.TableContainer sx={{ overflowX: "auto" }}>
        <MUI.Table>
          <MUI.TableHead>
            <MUI.TableRow
              sx={{
                "& th": {
                  bgcolor: (theme) =>
                    theme.palette.mode === "light"
                      ? "grey.50"
                      : "rgba(255,255,255,0.04)",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: "text.secondary",
                  borderBottom: "2px solid",
                  borderBottomColor: "divider",
                },
              }}
            >
              <MUI.TableCell>{t("Title")}</MUI.TableCell>
              <MUI.TableCell align="right" sx={{ width: 120 }}>
                {t("Actions")}
              </MUI.TableCell>
            </MUI.TableRow>
          </MUI.TableHead>
          <MUI.TableBody>
            {isLoading ? (
              <MUI.TableRow>
                <MUI.TableCell colSpan={2} sx={{ border: 0, py: 6 }}>
                  <MUI.Stack alignItems="center" justifyContent="center">
                    <MUI.CircularProgress size={28} />
                  </MUI.Stack>
                </MUI.TableCell>
              </MUI.TableRow>
            ) : items.length === 0 ? (
              <MUI.TableRow>
                <MUI.TableCell colSpan={2} sx={{ border: 0, py: 3 }}>
                  <SimpleAlert
                    severity="info"
                    message={emptyMessage}
                    testId={testIdPrefix && `${testIdPrefix}.empty-message`}
                  />
                </MUI.TableCell>
              </MUI.TableRow>
            ) : (
              items.map((item) => (
                <MUI.TableRow
                  hover
                  key={item.id}
                  sx={{ "&:last-child td": { borderBottom: 0 } }}
                  data-testid={testIdPrefix && `${testIdPrefix}.row.${item.id}`}
                >
                  <MUI.TableCell>
                    <MUI.Tooltip title={item.name}>
                      <MUI.Typography
                        variant="body2"
                        sx={{ fontWeight: 600 }}
                        data-testid={testIdPrefix && `${testIdPrefix}.row.${item.id}.name`}
                      >
                        {trimText(item.name, CELL_TRIM_LENGTH)}
                      </MUI.Typography>
                    </MUI.Tooltip>
                  </MUI.TableCell>
                  <MUI.TableCell align="right">
                    <MUI.IconButton
                      title={t("Edit")}
                      size="small"
                      onClick={() => onEdit(item)}
                      data-testid={testIdPrefix && `${testIdPrefix}.row.${item.id}.edit-button`}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </MUI.IconButton>
                    <MUI.Tooltip title={t("Delete")}>
                      <MUI.IconButton
                        size="small"
                        color="error"
                        onClick={() => onDelete(item)}
                        data-testid={testIdPrefix && `${testIdPrefix}.row.${item.id}.delete-button`}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </MUI.IconButton>
                    </MUI.Tooltip>
                  </MUI.TableCell>
                </MUI.TableRow>
              ))
            )}
          </MUI.TableBody>
        </MUI.Table>
      </MUI.TableContainer>
    </MUI.Paper>
  );
}
